import { useEffect, useState } from "react";
import { ShieldCheck } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/authStore";
import { fmtDateTime, shortId } from "../lib/format";
import TopBar from "../components/TopBar";
import EmptyState from "../components/EmptyState";
import { useToasts } from "../components/Toasts";

export default function LedgerPage() {
  const { user } = useAuth();
  const push = useToasts((s) => s.push);
  const isDirector = user?.role === "director";
  const [events, setEvents] = useState([]);
  const [filter, setFilter] = useState({ mission_id: "", type: "", actor_type: "" });
  const [verify, setVerify] = useState(null);
  const [verifying, setVerifying] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (filter.mission_id) params.set("mission_id", filter.mission_id);
    if (filter.type) params.set("type", filter.type);
    if (filter.actor_type) params.set("actor_type", filter.actor_type);
    params.set("limit", "200");
    const { data } = await api.get(`/ledger?${params.toString()}`);
    setEvents(data);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const runVerify = async () => {
    setVerifying(true);
    try {
      const { data } = await api.post("/ledger/verify");
      setVerify(data);
      push(data.ok ? `Chain OK · ${data.count} events` : `Chain BROKEN at ${data.broken_at_id}`, data.ok ? "ok" : "err");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      push(typeof detail === "string" ? detail : "Verify failed", "err");
    } finally {
      setVerifying(false);
    }
  };

  return (
    <>
      <TopBar
        title="Activity Ledger"
        subtitle="Every meaningful AI and human action, hash-chained."
        actions={
          isDirector ? (
            <button className="btn primary" onClick={runVerify} disabled={verifying} data-testid="verify-chain-btn">
              {verifying ? <span className="loader" /> : <><ShieldCheck size={14} /> Verify chain</>}
            </button>
          ) : null
        }
      />

      {verify ? (
        <div className={`panel`} style={{ marginBottom: 14 }} data-testid="verify-result">
          <div className="panel-body">
            <span className={`chain-status ${verify.ok ? "ok" : "err"}`}>
              {verify.ok ? "✓" : "✗"} {verify.ok ? `Chain valid` : `Chain broken`} — {verify.count} events
            </span>
            {!verify.ok ? <span className="hint" style={{ marginLeft: 12 }}>at #{verify.broken_at_index} · {verify.reason}</span> : null}
          </div>
        </div>
      ) : null}

      <div className="panel">
        <div className="filters">
          <div className="field">
            <label style={{ marginBottom: 3 }}>Mission ID</label>
            <input placeholder="uuid" value={filter.mission_id} onChange={(e) => setFilter({ ...filter, mission_id: e.target.value })} data-testid="ledger-mission-filter" />
          </div>
          <div className="field">
            <label style={{ marginBottom: 3 }}>Type</label>
            <input placeholder="e.g. mission.created" value={filter.type} onChange={(e) => setFilter({ ...filter, type: e.target.value })} data-testid="ledger-type-filter" />
          </div>
          <div className="field">
            <label style={{ marginBottom: 3 }}>Actor type</label>
            <select value={filter.actor_type} onChange={(e) => setFilter({ ...filter, actor_type: e.target.value })} data-testid="ledger-actor-filter">
              <option value="">Any</option>
              <option value="user">user</option>
              <option value="agent">agent</option>
              <option value="system">system</option>
            </select>
          </div>
          <span className="hint">{events.length} events</span>
        </div>

        {events.length === 0 ? (
          <EmptyState title="No events match this filter." />
        ) : (
          <table className="ledger-table" data-testid="ledger-table">
            <thead>
              <tr><th>TS</th><th>ACTOR</th><th>TYPE</th><th>PAYLOAD</th><th>HASH</th></tr>
            </thead>
            <tbody>
              {events.map((e) => (
                <tr key={e.id}>
                  <td className="hint">{fmtDateTime(e.ts)}</td>
                  <td>{e.actor_type}·{shortId(e.actor_id)}</td>
                  <td className="type">{e.type}</td>
                  <td className="payload-preview">{JSON.stringify(e.payload)}</td>
                  <td className="hash">{e.hash.slice(0, 12)}…</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}
